/*
 * Copyright (c) 2021 NOOGA DEVELOPMENT, All rights reserved.
 * Author Raman Kremko <raman.kremko@noogadev.com>
 */

import { ShortListsAction, ShortListsActionType } from '../actions/AShortLists';
import {
    StoreTypeResource,
    CashRegisterModelShortDto,
    OperatorShortDto,
    FirmwareVersionDTO,
    TaxAuthorityDTO,
    TaxationTypeDTO,
    CityResource,
    RegionResource,
    FiscalDriverVersionDto,
    ActivityDto,
} from '../api/shortLists/shortListsClient.typings';
import { ShortUserDto } from '../api/users/usersClient.typings';
import { translateOrLeaveAzAndSortByProp, sortByProp, sortVersions } from '../../utils/utils';
import { LocaleId } from '../translation/i18n';
import { UserAction, UserActionType } from '../actions/userActions';

export interface ShortListsState {
    storeTypesList: StoreTypeResource[];
    mainActivitiesList: ActivityDto[];
    middleActivitiesList: ActivityDto[];
    lowerActivitiesList: ActivityDto[];
    cashRegisterModelsList: CashRegisterModelShortDto[];
    operatorsShortList: OperatorShortDto[];
    firmwareVersionsList: FirmwareVersionDTO[];
    fiscalDriverVersionsList: FiscalDriverVersionDto[];
    taxAuthorityList: TaxAuthorityDTO[];
    taxationTypesList: TaxationTypeDTO[];
    cities: CityResource[];
    regions: RegionResource[];
    alertAssigneeUsersList: ShortUserDto[];
    userIds: ShortUserDto['id'][];
    userById: Record<ShortUserDto['id'], ShortUserDto>;
}

const initialState: ShortListsState = {
    storeTypesList: [],
    mainActivitiesList: [],
    middleActivitiesList: [],
    lowerActivitiesList: [],
    cashRegisterModelsList: [],
    operatorsShortList: [],
    firmwareVersionsList: [],
    fiscalDriverVersionsList: [],
    taxAuthorityList: [],
    taxationTypesList: [],
    cities: [],
    regions: [],
    alertAssigneeUsersList: [],
    userById: {},
    userIds: [],
};

export function shortListsReducer(
    state: ShortListsState = initialState,
    action: ShortListsAction | UserAction,
): ShortListsState {
    switch (action.type) {
        case ShortListsActionType.FETCH_CASH_REGISTER_MODELS_LIST_SUCCESS:
            const cashRegisterModelsList = sortByProp(action.payload, 'name', LocaleId.En);

            return {
                ...state,
                cashRegisterModelsList,
            };

        case ShortListsActionType.FETCH_OPERATORS_LIST_SUCCESS:
            const sortedOperatorsShortList = sortByProp(action.payload, 'companyName', LocaleId.En);
            return {
                ...state,
                operatorsShortList: sortedOperatorsShortList,
            };

        case ShortListsActionType.FETCH_FIRMWARE_VERSIONS_LIST_SUCCESS:
            const sortFirmwareVersionsList = sortVersions<FirmwareVersionDTO>(action.payload, 'name', 'desc');

            return {
                ...state,
                firmwareVersionsList: sortFirmwareVersionsList,
            };

        case ShortListsActionType.FETCH_FISCAL_DRIVER_VERSIONS_LIST_SUCCESS:
            const sortFiscalDriverVersionsList = sortVersions<FiscalDriverVersionDto>(action.payload, 'name', 'desc');

            return {
                ...state,
                fiscalDriverVersionsList: sortFiscalDriverVersionsList,
            };

        case ShortListsActionType.FETCH_TAX_AUTHORITIES_LIST_SUCCESS:
            const sortedTaxAuthorityList = sortByProp(action.payload, 'name', LocaleId.En);

            return {
                ...state,
                taxAuthorityList: sortedTaxAuthorityList,
            };

        case ShortListsActionType.FETCH_TAXATION_TYPES_LIST_SUCCESS:
            return {
                ...state,
                taxationTypesList: action.payload,
            };

        case ShortListsActionType.FETCH_CITIES_LIST_SUCCESS:
            const sortedCities = sortByProp(action.payload, 'name', LocaleId.En);

            return {
                ...state,
                cities: sortedCities,
            };
        case ShortListsActionType.FETCH_REGIONS_LIST_SUCCESS:
            const sortedRegions = sortByProp(action.payload, 'name', LocaleId.En);

            return {
                ...state,
                regions: sortedRegions,
            };

        case ShortListsActionType.FETCH_USERS_LIST_SUCCESS:
            const userById: ShortListsState['userById'] = {};
            const userIds: ShortListsState['userIds'] = [];
            const sortedUsers = sortByProp(action.payload, 'firstName', LocaleId.En);

            sortedUsers.forEach((user) => {
                userById[user.id] = user;
                userIds.push(user.id);
            });
            return {
                ...state,
                userById,
                userIds,
                alertAssigneeUsersList: sortedUsers,
            };

        case ShortListsActionType.FETCH_STORE_TYPES_LIST_SUCCESS:
            const sortedStoreTypesList = translateOrLeaveAzAndSortByProp<StoreTypeResource>(
                action.payload,
                'store.storeTypes.',
                'name',
                //curent locale
            );
            return {
                ...state,
                storeTypesList: sortedStoreTypesList,
            };

        case ShortListsActionType.FETCH_MAIN_ACTIVITIES_LIST_SUCCESS:
            const sortedMainActivities = translateOrLeaveAzAndSortByProp<ActivityDto>(
                action.payload,
                'store.storeMainActivities.',
                'name',
                //current locale
            );

            return {
                ...state,
                mainActivitiesList: sortedMainActivities,
            };

        case ShortListsActionType.FETCH_MIDDLE_ACTIVITIES_LIST_SUCCESS:
            const sortedMiddleActivities = translateOrLeaveAzAndSortByProp<ActivityDto>(
                action.payload,
                'store.storeMiddleActivities.',
                'name',
                //current locale
            );

            return {
                ...state,
                middleActivitiesList: sortedMiddleActivities,
            };

        case ShortListsActionType.FETCH_LOWER_ACTIVITIES_LIST_SUCCESS:
            const sortedLowerActivities = translateOrLeaveAzAndSortByProp<ActivityDto>(
                action.payload,
                'store.storeLowerActivities.',
                'name',
                //current locale
            );

            return {
                ...state,
                lowerActivitiesList: sortedLowerActivities,
            };

        case UserActionType.CHANGE_LOCALE: {
            const sortedStoreTypesList = translateOrLeaveAzAndSortByProp<StoreTypeResource>(
                state.storeTypesList,
                'store.storeTypes.',
                'name',
                action.payload,
            );
            const sortedMainActivities = translateOrLeaveAzAndSortByProp<ActivityDto>(
                state.mainActivitiesList,
                'store.storeMainActivities.',
                'name',
                action.payload,
            );
            const sortedMiddleActivities = translateOrLeaveAzAndSortByProp<ActivityDto>(
                state.middleActivitiesList,
                'store.storeMiddleActivities.',
                'name',
                action.payload,
            );
            const sortedLowerActivities = translateOrLeaveAzAndSortByProp<ActivityDto>(
                state.lowerActivitiesList,
                'store.storeLowerActivities.',
                'name',
                action.payload,
            );

            return {
                ...state,
                storeTypesList: sortedStoreTypesList,
                mainActivitiesList: sortedMainActivities,
                middleActivitiesList: sortedMiddleActivities,
                lowerActivitiesList: sortedLowerActivities,
            };
        }

        default:
            break;
    }

    return state;
}
